import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { cases, consultingAreas, products } from './content'

export type Locale = 'pt' | 'en' | 'es' | 'zh'

export const languageOptions: Array<{ code: Locale; label: string; htmlLang: string; available: boolean }> = [
  { code: 'pt', label: 'PT', htmlLang: 'pt-BR', available: true },
  { code: 'en', label: 'EN', htmlLang: 'en', available: false },
  { code: 'es', label: 'ES', htmlLang: 'es', available: false },
  { code: 'zh', label: '中文', htmlLang: 'zh-Hans', available: false },
]

const pt = {
  skipLink: 'Pular para o conteúdo',
  navigation: {
    ariaLabel: 'Navegação principal',
    links: [
      ['Consultoria', '#consultoria'],
      ['Produtos & Soluções', '#produtos'],
      ['Cases', '#cases'],
      ['Sobre', '#sobre'],
      ['Contato', '#contato'],
      ['Bāxī Zhōngguó', 'https://bashizhongguo.com'],
    ] as Array<[string, string]>,
    contact: 'Fale com a GeniusLEAD',
    openMenu: 'Abrir menu',
    closeMenu: 'Fechar menu',
  },
  hero: {
    consultingKicker: 'CONSULTORIA',
    consultingTitle: 'Sua empresa cresceu.\nSua gestão precisa crescer também.',
    consultingCta: 'Conheça nossa atuação',
    productsKicker: 'PRODUTOS & SOLUÇÕES',
    productsTitle: 'Soluções criadas para problemas reais e validadas na operação.',
    productsMobileTitle: 'Soluções criadas para problemas reais.',
    productsSupport: 'Tecnologia validada na operação.',
    productsCta: 'Conheça as soluções',
  },
  consulting: {
    kicker: 'CONSULTORIA',
    title: 'Estrutura para uma Nova Escala',
    mobileTitle: 'Estrutura para escalar',
    introduction: <>O crescimento exige uma nova estrutura com <strong>pessoas</strong>, <strong>processos</strong> e <strong>gestão</strong> em alto nível.</>,
    mobileIntroduction: <>O crescimento exige <strong>pessoas</strong>, <strong>processos</strong> e <strong>gestão</strong> em alto nível.</>,
    selectorLabel: 'Áreas de consultoria',
    panelLabel: 'ÁREA DE ATUAÇÃO',
    focusLabel: 'Onde atuamos:',
    seeLabel: 'Veja',
    contact: 'Fale com a GeniusLEAD',
    areas: consultingAreas,
  },
  products: {
    kicker: 'PRODUTOS & SOLUÇÕES',
    title: 'Aplicativos, Serviços e Produtos validados na prática',
    mobileTitle: 'Soluções próprias',
    mediaLabel: 'Imagem do produto',
    problem: 'Problema',
    solution: 'Solução',
    gain: 'Ganho',
    items: products,
  },
  cases: {
    kicker: 'CASES & RESULTADOS',
    title: 'Estrutura para crescer. Resultados para comprovar.',
    mobileTitle: 'Resultados na prática',
    introduction: 'Projetos reais em que, tecnologia, pessoas e processos foram reorganizadas para sustentar uma nova escala, com resultados reais.',
    projects: 'Projetos',
    selectorLabel: 'Cases',
    mediaLabel: 'Imagem do case',
    structure: 'Estrutura criada',
    cta: 'Conheça nossa atuação',
    closingLabel: 'Empresas diferentes. O mesmo princípio.',
    closingTitle: 'Estrutura → Tecnologia → Pessoas → Capacidade → Resultado',
    closingText: 'A GeniusLEAD entra onde a empresa precisa evoluir — da gestão financeira ao chão de fábrica, do desenvolvimento de pessoas à tecnologia.',
    items: cases,
  },
  about: {
    kicker: 'SOBRE A GENIUSLEAD',
    title: 'Gestão para empresas que precisam evoluir.',
    paragraphs: [
      <>A GeniusLEAD atua na estruturação de empresas em crescimento, conectando <strong>gestão, tecnologia, processos e pessoas</strong> para aumentar eficiência, controle e capacidade de execução.</>,
      <>Nosso trabalho começa no diagnóstico, avança para a implantação e permanece próximo da operação até que a mudança funcione de verdade.</>,
      <>Atuamos em áreas como <strong>estratégia, financeiro, pessoas, comercial, operações, tecnologia e expansão</strong>, sempre com foco em resultado mensurável e crescimento sustentável.</>,
    ],
    quote: 'Eficiência como princípio. Execução como método.',
  },
  contact: {
    kicker: 'CONTATO',
    title: 'Vamos falar sobre o próximo passo da sua empresa.',
    introduction: 'Se o crescimento aumentou a complexidade da operação, a GeniusLEAD pode ajudar a estruturar o que vem depois.',
    specialties: 'Gestão · Tecnologia · Processos · Pessoas · Expansão',
    whatsapp: 'WhatsApp: +55 15 98115-1208',
    fields: { name: 'Nome', company: 'Empresa', contact: 'E-mail ou telefone', message: 'Mensagem' },
    submit: 'Fale com a GeniusLEAD',
    emailSubject: 'Contato pelo site GeniusLEAD',
  },
  footer: {
    signature: 'Gestão · Execução · Tecnologia · Crescimento',
    copyright: '© 2026 GeniusLEAD',
  },
}

export type SiteCopy = typeof pt

const dictionaries: Partial<Record<Locale, SiteCopy>> = { pt }

type LanguageContextValue = {
  locale: Locale
  copy: SiteCopy
  setLocale: (locale: Locale) => void
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

function initialLocale(): Locale {
  const pathLocale = window.location.pathname.split('/').filter(Boolean)[0] as Locale | undefined
  const storedLocale = window.localStorage.getItem('geniuslead-language') as Locale | null
  const candidate = pathLocale ?? storedLocale ?? 'pt'
  return languageOptions.some((option) => option.code === candidate && option.available) ? candidate : 'pt'
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale)

  useEffect(() => {
    const htmlLang = languageOptions.find((option) => option.code === locale)?.htmlLang ?? 'pt-BR'
    document.documentElement.lang = htmlLang
    window.localStorage.setItem('geniuslead-language', locale)
  }, [locale])

  const value = useMemo<LanguageContextValue>(() => ({
    locale,
    copy: dictionaries[locale] ?? pt,
    setLocale: (nextLocale) => {
      if (languageOptions.some((option) => option.code === nextLocale && option.available)) setLocaleState(nextLocale)
    },
  }), [locale])

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) throw new Error('useLanguage must be used inside LanguageProvider')
  return context
}

export function LanguageSelector() {
  const { locale, setLocale } = useLanguage()

  return (
    <div className="language-selector" aria-label="Selecionar idioma">
      {languageOptions.map((option, index) => (
        <span className="language-selector__option" key={option.code}>
          {index > 0 && <span aria-hidden="true">·</span>}
          <button
            type="button"
            className={locale === option.code ? 'active' : ''}
            aria-current={locale === option.code ? 'true' : undefined}
            aria-label={`${option.label}${option.available ? '' : ' — tradução em preparação'}`}
            title={option.available ? undefined : 'Tradução em preparação'}
            disabled={!option.available}
            onClick={() => setLocale(option.code)}
          >
            {option.label}
          </button>
        </span>
      ))}
    </div>
  )
}
